import { canSeePage, type PageKey } from '@/lib/permissions';
import type { UserRole } from '@/contexts/AuthContext';

/**
 * RBAC configuration model — the data behind /admin/rbac.
 *
 * WHAT THIS IS, AND WHAT IT IS NOT.
 * The app's live permissions are the hard-coded tables in `lib/permissions.ts`
 * (front end) and `requireRole` / `services/eisf/permissionMatrix.ts` /
 * `services/access/*` (back end). This module does NOT replace them. It is a
 * configuration surface that:
 *   - seeds itself from the live tables, so what you see first is the truth;
 *   - lets an admin express the intended grid per the client's Role x Module
 *     matrix, at CRUD granularity;
 *   - persists that as a DRAFT and exports it.
 *
 * Nothing here is enforced until the draft is wired to a backend store and the
 * guards read from it. That is deliberate: flipping enforcement is a change to
 * a shared contract with ~250 `requireRole` call sites behind it, and must be
 * done as its own piece of work.
 *
 * The live column in the UI is always recomputed from `canSeePage`, never from
 * the draft, so drift between intent and reality stays visible.
 */

export type CrudOp = 'create' | 'read' | 'update' | 'delete';

export interface CrudPerms {
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
}

/** The four levels from the client's matrix, plus CUSTOM for any other mix. */
export type AccessLevel = 'N' | 'V' | 'C' | 'A';
export type DisplayLevel = AccessLevel | 'CUSTOM';

export const CRUD_OPS: readonly CrudOp[] = ['create', 'read', 'update', 'delete'];

export const LEVEL_PRESETS: Record<AccessLevel, CrudPerms> = {
  // No access
  N: { create: false, read: false, update: false, delete: false },
  // View-only — read, no create/edit/delete
  V: { create: false, read: true, update: false, delete: false },
  // Create/Edit within the role's scope (operational use)
  C: { create: true, read: true, update: true, delete: false },
  // Admin/Configure — structure, settings, permissions, global actions
  A: { create: true, read: true, update: true, delete: true },
};

export const LEVEL_LABELS: Record<DisplayLevel, string> = {
  N: 'No access',
  V: 'View only',
  C: 'Create / Edit',
  A: 'Admin',
  CUSTOM: 'Custom',
};

export const LEVEL_HINTS: Record<DisplayLevel, string> = {
  N: 'Hidden and blocked.',
  V: 'Read, no create / edit / delete.',
  C: "Create and edit within the role's scope.",
  A: 'Full control, including delete and configuration.',
  CUSTOM: 'A mix that matches none of the four standard levels.',
};

export const EMPTY_PERMS: CrudPerms = LEVEL_PRESETS.N;

/** Which of the four levels this CRUD mix is, or CUSTOM. */
export function levelOf(p: CrudPerms): DisplayLevel {
  for (const level of ['N', 'V', 'C', 'A'] as const) {
    const preset = LEVEL_PRESETS[level];
    if (
      preset.create === p.create &&
      preset.read === p.read &&
      preset.update === p.update &&
      preset.delete === p.delete
    ) {
      return level;
    }
  }
  return 'CUSTOM';
}

/**
 * Read is the base of every other operation — you cannot create, edit or
 * delete what you cannot see. Applied on every toggle so the grid can never
 * express a permission the guards could not honour.
 */
export function normalisePerms(p: CrudPerms): CrudPerms {
  const anyWrite = p.create || p.update || p.delete;
  return anyWrite ? { ...p, read: true } : p;
}

// ---------------------------------------------------------------------------
// Modules — mirrors the left side panel (components/layout/AppShell.tsx)
// ---------------------------------------------------------------------------

export interface RbacModule {
  /** The PageKey the live permission tables are keyed on. */
  key: PageKey;
  label: string;
  /** Sidebar items that resolve to this page key, where more than one does. */
  note?: string;
}

export interface RbacModuleGroup {
  label: string;
  modules: RbacModule[];
}

/**
 * Grouped exactly as the sidebar groups them, plus a final group for pages
 * that carry real permissions but have no sidebar link — leaving those out
 * would make the configuration look complete when it is not.
 */
export const RBAC_MODULE_GROUPS: readonly RbacModuleGroup[] = [
  {
    label: 'Overview',
    modules: [{ key: 'dashboard', label: 'Dashboard' }],
  },
  {
    label: 'Clinical',
    modules: [
      { key: 'studies', label: 'Studies' },
      { key: 'subjects', label: 'Subjects' },
      { key: 'visits', label: 'Visit Calendar' },
      { key: 'forms', label: 'Forms' },
      { key: 'esource', label: 'eSource' },
      { key: 'queries', label: 'Queries' },
    ],
  },
  {
    label: 'Documentation',
    modules: [
      { key: 'binder', label: 'eISF Binder' },
      { key: 'approvals', label: 'Approval Needed' },
      { key: 'delegations', label: 'Delegations' },
      { key: 'search', label: 'Search' },
      { key: 'signature-queue', label: 'eSignature Queue' },
      { key: 'document-library', label: 'Document Library' },
    ],
  },
  {
    label: 'Administration',
    modules: [
      { key: 'team', label: 'Site Team' },
      { key: 'training', label: 'Training' },
      { key: 'finance', label: 'Finance' },
      { key: 'compliance', label: 'Compliance' },
    ],
  },
  {
    label: 'Platform Admin',
    modules: [
      { key: 'platform-admin', label: 'Platform Admin', note: 'Sites, Users, Platform Audit Log' },
      { key: 'blinding', label: 'Blinding' },
    ],
  },
  {
    label: 'Master Data',
    modules: [
      {
        key: 'build-master-data',
        label: 'Master Data',
        note: 'eISF Catalog, DoA Responsibilities, Binder Templates',
      },
      { key: 'regulatory-tags', label: 'Regulatory Tags' },
      { key: 'sponsors', label: 'Sponsors' },
    ],
  },
  {
    label: 'No sidebar link',
    modules: [
      { key: 'studies.create', label: 'Create a study' },
      { key: 'studies.workspace', label: 'Study workspace' },
      { key: 'studies.onboarding', label: 'Study onboarding' },
      { key: 'doa', label: 'Delegation of Authority' },
      { key: 'site-documentation', label: 'Site Documentation' },
      { key: 'staff-documentation', label: 'Staff Documentation' },
      { key: 'monitor-assignments', label: 'Monitor Assignments' },
      { key: 'field-templates', label: 'Field Templates' },
    ],
  },
];

export const ALL_MODULES: readonly RbacModule[] = RBAC_MODULE_GROUPS.flatMap((g) => g.modules);

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

export interface RbacRoleGroup {
  label: string;
  note?: string;
  roles: UserRole[];
}

/**
 * Grouped by tier, matching the `Role` union in the backend's types/domain.ts.
 * The five sponsor / regulatory personas are reserved names that no guard
 * enforces today — flagged so nobody configures them expecting an effect.
 */
export const RBAC_ROLE_GROUPS: readonly RbacRoleGroup[] = [
  {
    label: 'Platform',
    roles: ['platform_admin', 'application_admin', 'compliance', 'study_designer'],
  },
  {
    label: 'Site',
    roles: [
      'site_admin',
      'principal_investigator',
      'sub_investigator',
      'coordinator',
      'site_study_designer',
      'regulatory_specialist',
      'site_pharma',
      'finance_billing',
      'monitor',
      'auditor',
      'clinical_nurse',
      'clinical_lab_assistant',
      'clinical_qe',
      'external_lab',
    ],
  },
  {
    label: 'Base',
    note: 'The default role a user holds after invite, before any study delegation.',
    roles: ['site_user'],
  },
  {
    label: 'Sponsor / regulatory',
    note: 'Reserved names. No guard enforces these today, so changes here have no effect yet.',
    roles: [
      'irb_administrator',
      'sponsor_regulatory_affairs',
      'regulatory_inspector',
      'data_manager',
      'sponsor_project_manager',
    ],
  },
];

export const ALL_ROLES: readonly UserRole[] = RBAC_ROLE_GROUPS.flatMap((g) => g.roles);

export const UNENFORCED_ROLES: ReadonlySet<UserRole> = new Set(
  RBAC_ROLE_GROUPS.find((g) => g.label === 'Sponsor / regulatory')?.roles ?? [],
);

// ---------------------------------------------------------------------------
// The matrix
// ---------------------------------------------------------------------------

/** role -> pageKey -> CRUD */
export type RbacMatrix = Record<string, Record<string, CrudPerms>>;

/**
 * What the app enforces right now. `canSeePage` is binary, so it can only
 * tell us N or "has access" — it cannot distinguish V from C from A. Seeding
 * grants READ only, and the admin raises it from there. Guessing C or A here
 * would silently invent permissions nobody granted.
 */
export function liveLevelFor(role: UserRole, key: PageKey): 'N' | 'V' {
  return canSeePage(role, key) ? 'V' : 'N';
}

export function seedMatrix(): RbacMatrix {
  const out: RbacMatrix = {};
  for (const role of ALL_ROLES) {
    out[role] = {};
    for (const m of ALL_MODULES) {
      out[role][m.key] = { ...LEVEL_PRESETS[liveLevelFor(role, m.key)] };
    }
  }
  return out;
}

export function permsFor(matrix: RbacMatrix, role: UserRole, key: PageKey): CrudPerms {
  return matrix[role]?.[key] ?? EMPTY_PERMS;
}

/** True when this cell no longer matches what the app actually enforces. */
export function isDrifted(matrix: RbacMatrix, role: UserRole, key: PageKey): boolean {
  const configured = levelOf(permsFor(matrix, role, key));
  const live = liveLevelFor(role, key);
  if (live === 'N') return configured !== 'N';
  // Live grants access; anything that still reads counts as agreeing.
  return !permsFor(matrix, role, key).read;
}

export function countDrift(matrix: RbacMatrix): number {
  let n = 0;
  for (const role of ALL_ROLES) {
    for (const m of ALL_MODULES) if (isDrifted(matrix, role, m.key)) n += 1;
  }
  return n;
}

// ---------------------------------------------------------------------------
// Draft persistence
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'dreamit.rbac.draft.v1';

export function loadDraft(): RbacMatrix | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RbacMatrix;
    if (!parsed || typeof parsed !== 'object') return null;
    // Fill any role/module added since the draft was saved, so a stale draft
    // degrades to "seeded" for the new cells rather than throwing.
    const seeded = seedMatrix();
    for (const role of ALL_ROLES) {
      seeded[role] = { ...seeded[role], ...(parsed[role] ?? {}) };
    }
    return seeded;
  } catch {
    return null;
  }
}

export function saveDraft(matrix: RbacMatrix): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(matrix));
    return true;
  } catch {
    return false;
  }
}

export function clearDraft(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private window / blocked storage — nothing to clear */
  }
}

/** The draft as a portable document, for handing to the backend later. */
export function exportMatrix(matrix: RbacMatrix): string {
  const rows = ALL_ROLES.map((role) => ({
    role,
    modules: Object.fromEntries(
      ALL_MODULES.map((m) => {
        const p = permsFor(matrix, role, m.key);
        return [m.key, { level: levelOf(p), ...p }];
      }),
    ),
  }));
  return JSON.stringify(
    { version: 1, generated_at: new Date().toISOString(), roles: rows },
    null,
    2,
  );
}
